import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Upload, Loader2, Sparkles, ImageIcon, Palette } from 'lucide-react';
import { toast } from 'sonner';

// Resize + JPEG-encode banner images to keep the /auth/profile payload tiny.
// Banners are ultra-wide (~3:1) so we target 1600×540 max.
async function fileToDataUrl(file, maxWidth = 1600, quality = 0.82) {
  if (!file.type.startsWith('image/')) throw new Error('Please choose an image file');
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('Could not read image'));
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxWidth / img.width);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

const GRADIENT_PRESETS = [
  { id: 'cosmic',   value: 'linear-gradient(135deg,#7B2FF7 0%,#00C6FF 50%,#00F5D4 100%)', label: 'Cosmic' },
  { id: 'aurora',   value: 'linear-gradient(135deg,#F472B6 0%,#7B2FF7 50%,#00C6FF 100%)', label: 'Aurora' },
  { id: 'sunset',   value: 'linear-gradient(135deg,#FBBF24 0%,#FB923C 50%,#7B2FF7 100%)', label: 'Sunset' },
  { id: 'ocean',    value: 'linear-gradient(135deg,#0EA5E9 0%,#6366F1 50%,#8B5CF6 100%)', label: 'Ocean' },
  { id: 'forest',   value: 'linear-gradient(135deg,#10B981 0%,#059669 50%,#0891B2 100%)', label: 'Forest' },
  { id: 'ember',    value: 'linear-gradient(135deg,#F97316 0%,#EF4444 50%,#DB2777 100%)', label: 'Ember' },
  { id: 'nebula',   value: 'linear-gradient(135deg,#8B5CF6 0%,#EC4899 50%,#F59E0B 100%)', label: 'Nebula' },
  { id: 'midnight', value: 'linear-gradient(135deg,#0F172A 0%,#1E293B 50%,#334155 100%)', label: 'Midnight' },
  { id: 'candy',    value: 'linear-gradient(135deg,#FCA5A5 0%,#FBBF24 50%,#86EFAC 100%)', label: 'Candy' },
];

/**
 * BannerPicker — dedicated dialog for the profile banner (the "color bar" at
 * the top of the profile page). Supports two modes:
 *   • Gradient preset (9 tasteful cosmic gradients)
 *   • Custom upload (client-resized to 1600px wide JPEG)
 *
 * onSave receives the chosen value (either a `linear-gradient(...)` string or
 * a `data:image/jpeg;base64,...` data URL) and should PUT /auth/profile with
 * `{ banner: value }`.
 */
export default function BannerPicker({ open, onOpenChange, currentBanner, onSave }) {
  const [picked, setPicked] = useState(currentBanner || GRADIENT_PRESETS[0].value);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const isImage = (v) => typeof v === 'string' && v.startsWith('data:image');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Banner too large (max 12MB). Please choose a smaller image.');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      setPicked(dataUrl);
      toast.success('Banner ready — click Save to apply');
    } catch (err) {
      toast.error(err.message || 'Could not load image');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!picked || picked === currentBanner) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    const ok = await onSave(picked);
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#161622] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="banner-picker-dialog"
      >
        <DialogTitle className="font-display flex items-center gap-2">
          <Palette size={16} className="text-fuchsia-400" />
          Change your banner
        </DialogTitle>
        <DialogDescription className="text-white/50 text-xs">
          Pick a preset gradient or upload your own image. Your banner is separate from your profile picture.
        </DialogDescription>

        {/* Preview */}
        <div
          className="mt-2 rounded-xl overflow-hidden border border-white/10 aspect-[4/1] relative"
          style={{ background: picked, backgroundSize: 'cover', backgroundPosition: 'center' }}
          data-testid="banner-picker-preview"
        >
          <div className="absolute inset-0 bg-black/25" />
          <div className="absolute bottom-2 left-3 text-[10px] uppercase tracking-widest text-white/70 bg-black/30 backdrop-blur px-2 py-0.5 rounded-full">
            {isImage(picked) ? 'Custom image' : 'Gradient preset'}
          </div>
        </div>

        {/* Upload */}
        <div className="mt-3">
          <label className="text-[11px] uppercase tracking-wider text-white/50">Upload your own</label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
            data-testid="banner-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="banner-upload-btn"
            disabled={uploading}
            className="mt-1 w-full rounded-xl border-2 border-dashed border-white/15 hover:border-fuchsia-400/50 bg-white/5 hover:bg-white/10 transition py-4 flex flex-col items-center justify-center text-white/70 hover:text-white disabled:opacity-50"
          >
            {uploading ? (
              <><Loader2 size={16} className="animate-spin mb-1" /> Reading…</>
            ) : (
              <>
                <Upload size={16} className="mb-1" />
                <span className="text-xs font-semibold">Choose an image</span>
                <span className="text-[10px] text-white/40 mt-0.5">JPG, PNG or WebP · max 12MB</span>
              </>
            )}
          </button>
        </div>

        {/* Gradient presets */}
        <div className="mt-4">
          <label className="text-[11px] uppercase tracking-wider text-white/50 flex items-center gap-1.5">
            <Sparkles size={11} className="text-fuchsia-400" /> Or pick a gradient
          </label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {GRADIENT_PRESETS.map(g => (
              <button
                key={g.id}
                onClick={() => setPicked(g.value)}
                data-testid={`banner-preset-${g.id}`}
                className={`aspect-[4/1] rounded-lg border-2 transition ${
                  picked === g.value ? 'border-white ring-2 ring-white/40' : 'border-transparent hover:border-white/30'
                }`}
                style={{ background: g.value }}
                title={g.label}
                aria-label={`${g.label} gradient`}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-end gap-2 pt-3 border-t border-white/5">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="banner-picker-cancel"
            className="text-white/70 hover:text-white hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !picked}
            data-testid="banner-picker-save"
            className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white border-0"
          >
            {saving ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</>
            ) : (
              <><ImageIcon size={14} className="mr-2" /> Apply banner</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
